import React, { useState, useEffect, useRef } from 'react';
import {
  Container, Paper, Typography, Box, TextField, Button,
  IconButton, Avatar, CircularProgress, Dialog, DialogContent, Menu, MenuItem
} from '@mui/material';
import { Send, AttachFile, Person, Close, InsertDriveFile, MoreVert, Delete } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import AppHeader from '../components/AppHeader';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function ChatPage() {
  const navigate = useNavigate();
  const { receiverId } = useParams();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [receiverName, setReceiverName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadMessages();
    loadReceiverInfo();
    const interval = setInterval(loadMessages, 3000);
    return () => clearInterval(interval);
  }, [receiverId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadReceiverInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/users/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const receiver = response.data.users.find(u => u.username === receiverId);
      if (receiver) setReceiverName(receiver.full_name);
    } catch (error) {
      console.error('Error loading receiver info:', error);
    }
  };

  const loadMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const conversationMessages = response.data.messages.filter(msg => 
        (msg.sender_id === user?.username && msg.receiver_id === receiverId) ||
        (msg.sender_id === receiverId && msg.receiver_id === user?.username) ||
        msg.receiver_id === 'all'
      );
      
      setMessages(conversationMessages);
      
      conversationMessages.forEach(async (msg) => {
        if (msg.receiver_id === user?.username && !msg.is_read) {
          try {
            await axios.post(
              `${API_URL}/messages/${msg._id}/mark-read`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } catch (error) {
            console.error('Error marking message as read:', error);
          }
        }
      });
      
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/heic', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      alert('Nur JPG, PNG, HEIC und PDF Dateien sind erlaubt');
      return;
    }

    const maxSize = file.type === 'application/pdf' ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      const maxMB = file.type === 'application/pdf' ? 10 : 5;
      alert(`Datei zu groß. Maximum: ${maxMB}MB`);
      return;
    }

    setSelectedFile(file);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && !selectedFile) return;

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      let fileUrl = null;
      let fileName = null;
      let messageType = 'text';

      if (selectedFile) {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', selectedFile);

        const uploadResponse = await axios.post(
          `${API_URL}/messages/upload`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        fileUrl = uploadResponse.data.file_url;
        fileName = uploadResponse.data.file_name;
        messageType = selectedFile.type.startsWith('image/') ? 'image' : 'pdf';
        setUploading(false);
      }

      const messageData = {
        receiver_id: receiverId,
        message_type: messageType,
        content: newMessage.trim() || fileName
      };

      if (fileUrl) {
        messageData.file_url = fileUrl;
        messageData.file_name = fileName;
      }

      await axios.post(
        `${API_URL}/messages`,
        messageData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNewMessage('');
      setSelectedFile(null);
      loadMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Fehler beim Senden der Nachricht');
    } finally {
      setSending(false);
      setUploading(false);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Nachricht wirklich löschen?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadMessages();
      setAnchorEl(null);
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Fehler beim Löschen der Nachricht');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Heute';
    if (date.toDateString() === yesterday.toDateString()) return 'Gestern';
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const groupMessagesByDate = (messages) => {
    const groups = {};
    messages.forEach(msg => {
      const date = formatDate(msg.created_at);
      if (!groups[date]) groups[date] = [];
      groups[date].push(msg);
    });
    return groups;
  };

  const messageGroups = groupMessagesByDate(messages);

  const renderMessageContent = (msg) => {
    const isSent = msg.sender_id === user?.username;

    if (msg.message_type === 'image' && msg.file_url) {
      return (
        <Box>
          {msg.content && msg.content !== msg.file_name && (
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
              {msg.content}
            </Typography>
          )}
          <Box
            component="img"
            src={`${API_URL}${msg.file_url}`}
            alt={msg.file_name}
            sx={{
              maxWidth: '100%',
              maxHeight: '300px',
              borderRadius: 1,
              cursor: 'pointer'
            }}
            onClick={() => setImagePreview(`${API_URL}${msg.file_url}`)}
          />
        </Box>
      );
    }

    if (msg.message_type === 'pdf' && msg.file_url) {
      return (
        <Box>
          {msg.content && msg.content !== msg.file_name && (
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
              {msg.content}
            </Typography>
          )}
          <Paper
            sx={{
              p: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: isSent ? 'rgba(255,255,255,0.2)' : '#f5f5f5',
              cursor: 'pointer'
            }}
            onClick={() => window.open(`${API_URL}${msg.file_url}`, '_blank')}
          >
            <InsertDriveFile />
            <Typography variant="body2">{msg.file_name}</Typography>
          </Paper>
        </Box>
      );
    }

    return (
      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
        {msg.content}
      </Typography>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <AppHeader 
        title={receiverName || receiverId} 
        showBack={true} 
        backTo="/messages" 
      />

      <Box sx={{ flexGrow: 1, overflow: 'auto', bgcolor: '#f5f5f5', p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {Object.keys(messageGroups).length === 0 ? (
              <Box sx={{ textAlign: 'center', p: 4, color: '#757575' }}>
                <Typography>Noch keine Nachrichten</Typography>
                <Typography variant="body2">Sende die erste Nachricht!</Typography>
              </Box>
            ) : (
              Object.entries(messageGroups).map(([date, msgs]) => (
                <Box key={date}>
                  <Box sx={{ textAlign: 'center', my: 2 }}>
                    <Typography variant="caption" sx={{ 
                      bgcolor: '#e0e0e0', 
                      px: 2, 
                      py: 0.5, 
                      borderRadius: 2,
                      display: 'inline-block'
                    }}>
                      {date}
                    </Typography>
                  </Box>
                  {msgs.map((msg, index) => {
                    const isSent = msg.sender_id === user?.username;
                    const isBroadcast = msg.receiver_id === 'all';
                    const hasAttachment = msg.message_type === 'image' || msg.message_type === 'pdf';
                    
                    return (
                      <Box
                        key={msg._id || index}
                        sx={{
                          display: 'flex',
                          justifyContent: isSent ? 'flex-end' : 'flex-start',
                          mb: 1,
                          alignItems: 'flex-start'
                        }}
                      >
                        <Paper
                          sx={{
                            p: 1.5,
                            maxWidth: '70%',
                            bgcolor: isSent ? '#1976d2' : '#fff',
                            color: isSent ? '#fff' : '#000',
                            borderRadius: 2,
                            boxShadow: 1,
                            position: 'relative'
                          }}
                        >
                          {!isSent && (
                            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                              {msg.sender_name}
                            </Typography>
                          )}
                          {isBroadcast && (
                            <Typography variant="caption" sx={{ 
                              display: 'block', 
                              mb: 0.5, 
                              fontStyle: 'italic',
                              opacity: 0.8
                            }}>
                              📢 Broadcast an alle
                            </Typography>
                          )}
                          {renderMessageContent(msg)}
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>
                            {formatTime(msg.created_at)}
                          </Typography>
                        </Paper>
                        {(isSent || hasAttachment) && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              setAnchorEl(e.currentTarget);
                              setSelectedMsg(msg);
                            }}
                            sx={{ ml: isSent ? 0.5 : 0, mr: isSent ? 0 : 0.5, order: isSent ? 1 : -1 }}
                          >
                            <MoreVert fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ))
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      <Paper sx={{ p: 2, borderTop: '1px solid #e0e0e0' }} elevation={3}>
        {selectedFile && (
          <Box sx={{ mb: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2">
              {selectedFile.type.startsWith('image/') ? '🖼️' : '📄'} {selectedFile.name}
            </Typography>
            <IconButton size="small" onClick={() => setSelectedFile(null)}>
              <Close />
            </IconButton>
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/jpeg,image/png,image/heic,application/pdf"
            onChange={handleFileSelect}
          />
          <IconButton
            color="primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending || uploading}
          >
            <AttachFile />
          </IconButton>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder="Nachricht eingeben..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={sending || uploading}
            variant="outlined"
            size="small"
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSendMessage}
            disabled={(!newMessage.trim() && !selectedFile) || sending || uploading}
            sx={{ minWidth: '56px', height: '56px' }}
          >
            {sending || uploading ? <CircularProgress size={24} color="inherit" /> : <Send />}
          </Button>
        </Box>
      </Paper>

      {/* Message Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {selectedMsg?.message_type === 'image' && selectedMsg?.file_url && (
          <MenuItem onClick={() => {
            const link = document.createElement('a');
            link.href = `${API_URL}${selectedMsg.file_url}`;
            link.download = selectedMsg.file_name || 'image.jpg';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setAnchorEl(null);
          }}>
            <InsertDriveFile fontSize="small" sx={{ mr: 1 }} />
            Herunterladen
          </MenuItem>
        )}
        {selectedMsg?.message_type === 'pdf' && selectedMsg?.file_url && (
          <MenuItem onClick={() => {
            window.open(`${API_URL}${selectedMsg.file_url}`, '_blank');
            setAnchorEl(null);
          }}>
            <InsertDriveFile fontSize="small" sx={{ mr: 1 }} />
            PDF öffnen
          </MenuItem>
        )}
        {selectedMsg?.sender_id === user?.username && (
          <MenuItem onClick={() => {
            if (selectedMsg) handleDeleteMessage(selectedMsg._id);
          }}>
            <Delete fontSize="small" sx={{ mr: 1 }} />
            Löschen
          </MenuItem>
        )}
      </Menu>

      {/* Image Preview Dialog */}
      <Dialog
        open={!!imagePreview}
        onClose={() => setImagePreview(null)}
        maxWidth="lg"
      >
        <DialogContent>
          <Box sx={{ position: 'relative' }}>
            <IconButton
              sx={{ position: 'absolute', top: 0, right: 0, color: 'white', bgcolor: 'rgba(0,0,0,0.5)' }}
              onClick={() => setImagePreview(null)}
            >
              <Close />
            </IconButton>
            <img src={imagePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '80vh' }} />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
